import {inject, injectable} from 'inversify'
import {DI} from '@/src/config/di/types'
import type {IBasketRepository} from '@/src/core/domain/repositories'
import {BasketError} from '@/src/core/domain/errors'
import {OperationResult, Result} from '@/src/shared/utils/result'
import {BasketData, SyncBasketItem} from '@/src/entities/basket/types'
import type {IErrorReporter} from '@/src/ports/services/monitoring'

/**
 * Sync Basket Use Case
 *
 * Orchestrates bulk synchronization of basket items.
 * Used for debounced batch updates when multiple items change.
 * Validates all items before syncing.
 *
 * @example
 * const result = await syncBasketUseCase.execute(items)
 * if (result.isOk()) {
 *   // Update UI with new basket data
 * }
 */
@injectable()
export class SyncBasketUseCase {
  @inject(DI.IBasketRepository)
  basketRepository!: IBasketRepository

  @inject(DI.IErrorReporter)
  errorReporter!: IErrorReporter

  /**
   * Sync multiple basket items at once
   */
  async execute(items: SyncBasketItem[]): Promise<OperationResult<BasketData, BasketError>> {
    if (items.length === 0) {
      return Result.fail(new BasketError('SYNC_FAILED'))
    }

    try {
      const basketData = await this.basketRepository.sync(items)
      return Result.ok(basketData)
    } catch (error) {
      this.errorReporter.capture(error instanceof Error ? error : new Error(String(error)), {
        items_count: items.length,
      })
      return Result.fail(new BasketError('SYNC_FAILED', error))
    }
  }
}
